import type { HTMLAttributes, ReactNode } from "react";

interface TextProps extends HTMLAttributes<HTMLElement> {
  readonly children: ReactNode;
}

/** A section eyebrow. Uppercasing happens in CSS so callers write sentences. */
export function Label({ children, className = "", ...rest }: TextProps) {
  return (
    <p className={`label ${className}`} {...rest}>
      {children}
    </p>
  );
}

interface DisplayProps extends TextProps {
  readonly size?: "xl" | "lg" | "md";
  readonly as?: "h1" | "h2" | "p";
}

/** Phone sizes first; a desktop has the room for a size up. */
const DISPLAY_SIZE: Record<NonNullable<DisplayProps["size"]>, string> = {
  xl: "text-[34px] leading-[1.1] md:text-[44px]",
  lg: "text-[26px] leading-[1.2] md:text-[32px]",
  md: "text-[20px] leading-[1.3] md:text-[24px]",
};

/** Serif display: the sentence a screen is about. */
export function Display({ children, size = "lg", as: Tag = "h1", className = "", ...rest }: DisplayProps) {
  return (
    <Tag className={`font-display tracking-[-0.02em] ${DISPLAY_SIZE[size]} ${className}`} {...rest}>
      {children}
    </Tag>
  );
}

/** Prose. */
export function Body({ children, className = "", ...rest }: TextProps) {
  return (
    <p className={`text-[15px] leading-[1.5] md:text-[16px] ${className}`} {...rest}>
      {children}
    </p>
  );
}

/** The small print: a caveat or an honest footnote. */
export function Tiny({ children, className = "", ...rest }: TextProps) {
  return (
    <p className={`text-[12.5px] leading-[1.5] text-mid md:text-[13.5px] ${className}`} {...rest}>
      {children}
    </p>
  );
}

/** Mono figures and readouts. */
export function Mono({ children, className = "", ...rest }: TextProps) {
  return (
    <span className={`font-mono text-[11px] tracking-[0.1em] ${className}`} {...rest}>
      {children}
    </span>
  );
}
